import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { subscribeMemberConversations, summariseUnread } from '../../services/memberConversationService';
import MelioBrandMark from '../brand/MelioBrandMark';
import { AppleIcon, ChatIcon, DocIcon, FlaskIcon, HomeIcon, PersonIcon, PulseIcon } from './memberPrimitives';

/**
 * Rail width. Exported so MemberShell offsets the content by the same number —
 * these were two independent literal `80`s that had to be kept in step by hand.
 */
export const RAIL_WIDTH = 96;

interface RailItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  activeWhen: (pathname: string) => boolean;
  badge?: number;
}

/**
 * Left rail — the only primary navigation in the member portal.
 *
 * Seven destinations: Home, Results, Plan, Daily, Messages, Testing, Profile.
 * Home is also the active state for the sub-surfaces that have no slot of their
 * own (Intelligence · Reports · History · Cycle · Body map). Sign-out lives
 * inside Profile.
 *
 * Results is second, directly under Home, and is the reason this rail grew from
 * six items to seven. Members told us they could not find their blood work
 * after a panel came back — correctly, because the rail had no word for it and
 * the only route was a query param on a tab inside Intelligence. A seventh slot
 * is a real cost in a 96px rail; being unable to find the thing you paid for is
 * a larger one.
 *
 * Labels are whole words. "Bite" and "Msgs" were coinages invented to fit an
 * 80px rail at 9px type — the rail is 96px now and the words are readable
 * instead of decoded.
 */
export default function MemberRail() {
  const location = useLocation();
  const { currentUser } = useAuth();
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    if (!currentUser) return;
    return subscribeMemberConversations(
      currentUser.uid,
      (conversations) => setUnreadMessages(summariseUnread(conversations).total),
      () => {},
    );
  }, [currentUser]);

  const items: RailItem[] = [
    {
      path: '/home',
      label: 'Home',
      icon: <HomeIcon size={22} />,
      // Home stands in for the sub-surfaces that have no rail slot. It must NOT
      // claim routes another item owns — '/plan' was in this list as well as
      // being Plan's own route, so both lit up at once on /plan and the rail
      // stopped telling the member where they were.
      activeWhen: (p) =>
        p === '/home' ||
        p === '/dashboard' ||
        p === '/body-map' ||
        p.startsWith('/body-map/') ||
        p === '/intelligence' ||
        p.startsWith('/intelligence/') ||
        p === '/reports' ||
        p.startsWith('/reports/') ||
        p === '/history' ||
        p === '/family-tree' ||
        p === '/health-survey' ||
        p === '/cycle',
    },
    {
      path: '/results',
      label: 'Results',
      icon: <PulseIcon size={22} />,
      // '/uploads' is a redirect to '/results' rather than a page, so it never
      // appears as a resolved pathname — but a member mid-redirect should not
      // see the rail flicker to no selection at all.
      activeWhen: (p) => p === '/results' || p.startsWith('/results/') || p === '/uploads',
    },
    {
      path: '/plan',
      label: 'Plan',
      icon: <DocIcon size={22} />,
      activeWhen: (p) => p === '/plan',
    },
    {
      path: '/today',
      label: 'Daily',
      icon: <AppleIcon size={22} />,
      activeWhen: (p) => p === '/today' || p.startsWith('/today/'),
    },
    {
      path: '/messages',
      label: 'Messages',
      icon: <ChatIcon size={22} />,
      activeWhen: (p) => p === '/messages' || p.startsWith('/messages/'),
      badge: unreadMessages,
    },
    {
      path: '/testing',
      label: 'Testing',
      icon: <FlaskIcon size={22} />,
      activeWhen: (p) => p === '/testing' || p.startsWith('/testing/'),
    },
    {
      path: '/profile',
      label: 'Profile',
      icon: <PersonIcon size={22} />,
      activeWhen: (p) => p === '/profile',
    },
  ];

  // Ask Melio is reached from the floating launcher (FeedbackLauncher, mounted
  // in MemberShell), not from the rail. A permanently visible button is more
  // discoverable than one more nav item, and it does not cost a slot in the
  // member's primary navigation — which matters more now the rail carries seven.
  // The /feedback route still exists for deep links.

  return (
    <aside
      className="fixed left-0 top-0 bottom-0 z-40 flex flex-col items-center"
      style={{
        width: RAIL_WIDTH,
        backgroundColor: 'var(--m-rail)',
        color: 'var(--m-bg)',
        paddingTop: 16,
        paddingBottom: 16,
      }}
    >
      {/* Brand v3 wordmark, bone on Night. The framed monogram it replaced is
          the mark that is still unchosen; the wordmark is the settled part.
          Sized by WIDTH rather than height: the rail is 96px and the wordmark
          is 4.10:1, so height follows from the space rather than the reverse.
          The box border is gone — a rule around a wordmark reads as a button. */}
      <Link
        to="/home"
        className="flex items-center justify-center mb-6 mt-1"
        style={{ width: 84, height: 22 }}
        aria-label="Melio home"
      >
        <MelioBrandMark variant="wordmark" height={20} onDark ariaLabel="Melio home" />
      </Link>

      <nav className="flex flex-col gap-1 w-full px-2" aria-label="Primary">
        {items.map((item) => {
          const active = item.activeWhen(location.pathname);
          return (
            <Link
              key={item.path}
              to={item.path}
              aria-current={active ? 'page' : undefined}
              className="flex flex-col items-center gap-1 py-3 transition-colors relative"
              style={{
                color: active ? 'var(--b-bone)' : 'rgba(247, 246, 242, 0.58)',
                backgroundColor: active ? 'rgba(247, 246, 242, 0.07)' : 'transparent',
                /* The deck marks the current page with a rule on the leading
                   edge, not with a filled shape. It survives at rail width,
                   costs no chrome, and keeps oxblood as a pointer rather than
                   a background. */
                boxShadow: active ? 'inset 3px 0 0 0 var(--b-vital-red)' : 'none',
              }}
            >
              <span className="relative">
                {item.icon}
                {item.badge && item.badge > 0 ? (
                  <span
                    className="absolute -top-1 -right-2 min-w-[16px] h-[16px] px-1 flex items-center justify-center text-[10px] font-semibold"
                    style={{ backgroundColor: 'var(--b-oxblood)', color: 'var(--b-bone)' }}
                  >
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                ) : null}
              </span>
              <span
                className="text-[10px] uppercase tracking-[0.16em]"
                style={{ fontWeight: active ? 600 : 500 }}
              >
                {item.label}
              </span>
            </Link>
          );
        })}
      </nav>

      <div className="mt-auto">
        {/* Was /enrollment, which renders the public signup + Stripe checkout
            page — an already-paying member tapping "Book" landed on an
            account-creation form. Every other Book affordance in the app
            already pointed at the real scheduler. */}
        <Link
          to="/book/clinical-history"
          className="flex items-center justify-center melio-lift"
          style={{
            width: 56,
            height: 44,
            backgroundColor: 'var(--b-vital-red)',
            color: 'var(--b-bone)',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            textAlign: 'center',
            lineHeight: 1.1,
            padding: 4,
          }}
          aria-label="Book a consult"
          title="Book a consult"
        >
          Book
        </Link>
      </div>
    </aside>
  );
}
